import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import { companySuggestionsTableHeaders } from './headers-config';
import type { CompanySearchSuggestion } from './company-suggestions-table';
import { useCallback, useMemo } from 'react';

import { trackMixpanelSearchResultClicked } from '@/lib/mixpanel/mixpanel';
import { CompanyRowMainCell } from './company-row-main-cell';
import { RelatedProjectsCount } from '@/features/companies/related-projects/related-projects-count';
import { getAbbreviateNumber } from '@/helpers/format-number';
import { formatDate } from '@/helpers/format-date';
import { colors } from '@/theme/colors';
import { TextCell } from '../enriched-table/text-cell';
import { SkeletonText } from '@/ui/table/infinite-table/skeleton-row';
import { CompanyActions } from './company-actions';
import { paths } from '@/routes/helpers/paths';
import { modalState } from '@/state/modal.state';
import { CellLink } from '../enriched-table/ui';
import { TagsPreview } from '@/features/tags/tags-preview';
import { useResponsiveTagsCount } from '@/features/tags/use-responsive-tags-count';
import { useSetAtom } from 'jotai';

export const useCompanySuggestionsTableConfig = (
  data: CompanySearchSuggestion[],
  isEnrichDataLoading: boolean,
) => {
  const setModal = useSetAtom(modalState);

  const onCompanyLinkInteract = useCallback((companyId: string) => {
    trackMixpanelSearchResultClicked({
      entity_id: companyId,
      entity_type: 'company',
      entry_surface: 'global_search_companies_table',
      is_recent: false,
    });
  }, []);

  const headers = useTranslateHeaders(
    companySuggestionsTableHeaders,
    'default',
    'globalSearch.suggestionsTable',
  );

  const maxVisibleTags = useResponsiveTagsCount();
  const rows = useMemo(() => {
    return data.map(({ basicData, enrichedData }) => {
      return {
        id: basicData.id,
        rowHover: true,
        rowBackground: colors.basics.white,
        rowBackgroundHover: colors.gray.c1,
        onClick: () => setModal(null),
        cells: [
          {
            value: (
              <CellLink
                onClick={() => onCompanyLinkInteract(basicData.id)}
                to={paths.company({ companyId: basicData.id })}
              >
                <CompanyRowMainCell
                  name={basicData.name}
                  location={enrichedData.location}
                  logoUrl={basicData.logoUrl}
                  keyOffering={enrichedData.keyOffering}
                  website={enrichedData.website}
                  linkedInUrl={enrichedData.linkedInUrl}
                  isEnrichDataLoading={isEnrichDataLoading}
                />
              </CellLink>
            ),
          },
          {
            value: (
              <CellLink
                onClick={() => onCompanyLinkInteract(basicData.id)}
                to={paths.company({ companyId: basicData.id })}
              >
                <TagsPreview
                  tags={enrichedData.tags}
                  isLoading={isEnrichDataLoading}
                  maxVisibleTags={maxVisibleTags}
                />
              </CellLink>
            ),
          },
          {
            value: isEnrichDataLoading ? (
              <SkeletonText />
            ) : (
              <CellLink
                onClick={() => onCompanyLinkInteract(basicData.id)}
                to={paths.company({ companyId: basicData.id })}
              >
                <RelatedProjectsCount
                  value={enrichedData.projectListingsCount ?? 0}
                  companyId={basicData.id}
                />
              </CellLink>
            ),
          },
          {
            value: (
              <CellLink
                onClick={() => onCompanyLinkInteract(basicData.id)}
                to={paths.company({ companyId: basicData.id })}
              >
                <TextCell
                  value={enrichedData.yearEstablished}
                  isLoading={isEnrichDataLoading}
                />
              </CellLink>
            ),
          },
          {
            value: (
              <CellLink
                onClick={() => onCompanyLinkInteract(basicData.id)}
                to={paths.company({ companyId: basicData.id })}
              >
                <TextCell
                  value={enrichedData.fundingStage}
                  isLoading={isEnrichDataLoading}
                />
              </CellLink>
            ),
          },
          {
            value: (
              <CellLink
                onClick={() => onCompanyLinkInteract(basicData.id)}
                to={paths.company({ companyId: basicData.id })}
              >
                <TextCell
                  value={
                    enrichedData.capitalRaised
                      ? `$${getAbbreviateNumber(enrichedData.capitalRaised, 1)}`
                      : ''
                  }
                  isLoading={isEnrichDataLoading}
                />
              </CellLink>
            ),
          },
          {
            value: (
              <CellLink
                onClick={() => onCompanyLinkInteract(basicData.id)}
                to={paths.company({ companyId: basicData.id })}
              >
                <TextCell
                  value={
                    enrichedData.lastEditedDate
                      ? formatDate(enrichedData.lastEditedDate, {
                          format: 'dd MMM yyyy',
                        })
                      : ''
                  }
                  isLoading={isEnrichDataLoading}
                />
              </CellLink>
            ),
          },
          {
            value: (
              <CompanyActions
                companyId={basicData.id}
                companyName={basicData.name}
              />
            ),
            hoverOnly: true,
          },
        ],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEnrichDataLoading, maxVisibleTags, onCompanyLinkInteract]);

  return {
    headers,
    rows,
  };
};
